/*
 * Local utility definitions – required because this script is loaded with
 * `defer` and may execute before bundle.js which normally provides these
 * functions globally.  The `typeof` guards ensure we never overwrite an
 * existing definition.
 */
if (typeof debounce === 'undefined') {
  function debounce(func, timeout = 300) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => { func.apply(this, args); }, timeout);
    };
  }
}

if (typeof onKeyUpEscape === 'undefined') {
  function onKeyUpEscape(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    const summaryElement = openDetailsElement.querySelector('summary');
    openDetailsElement.removeAttribute('open');
    summaryElement.setAttribute('aria-expanded', false);
    summaryElement.focus();
  }
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
      // mirrorCheckboxes('All-Filter-', 'Filter');
      mirrorCheckboxes();
      adjustFacetFieldsetWrapper();
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
      handleFacetsDisclosure();
      handleFacetListColumnCount();
      initCollectionSizes()
      updateProductGrid();
      setTimeout(() => {
        if (window._swat && typeof window._swat.initializeActionButtons === 'function') {
          window._swat.initializeActionButtons('#product-grid');
        }
      }, 100);
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCountDesktop').innerHTML;
    const container = document.getElementById('ProductCountDesktop');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    new Ajaxinate({
      container: '#product-grid',
      pagination: '#AjaxinatePagination'
    });
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));

  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
    adjustFacetFieldsetWrapper()
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting', '.mobile-facets__header', '.mobile-facets__footer'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    let searchParams = '';
    for (let [key, value] of formData.entries()) {
      if (searchParams.trim() !== '') {
        searchParams += '&';
      }
      searchParams += encodeURIComponent(key) + '=' + encodeURIComponent(value);
    }
    return searchParams;
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
  
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

customElements.define('facet-remove', FacetRemove);

// Control Overlay when details elements are open
function handleFacetsDisclosure() {
  const detailsElements = document.querySelectorAll('.facets__disclosure');
  const overlayElement = document.querySelector('.product-grid-overlay');
  const applyFiltersElements = document.querySelectorAll('.desktop-apply-filters');

  detailsElements.forEach((detailsElement) => {

    if (detailsElement.hasAttribute('open')) {
      overlayElement.style.display = 'block';
      
    }
    
      // Listen to the 'toggle' event on each details element
      detailsElement.addEventListener('toggle', function() {
          if (this.open) {
              overlayElement.style.display = 'block';
              document.querySelector('.active-facets').classList.add('show-selected-filters');
              detailsElements.forEach((detailsElement) => {
                  if (detailsElement !== this) {
                      detailsElement.removeAttribute('open');
                  }
              });
              adjustFacetFieldsetWrapper();
          } else {
              // Check if all other details elements are also closed.
              // If all are closed, hide the overlay.
              let allClosed = Array.from(detailsElements).every(el => !el.open);
              if (allClosed) {
                  overlayElement.style.display = 'none';
                  document.querySelector('.active-facets').classList.remove('show-selected-filters');
              }
          }
      });
  });

  overlayElement.addEventListener('click', function() {
      detailsElements.forEach((detailsElement) => {
          detailsElement.removeAttribute('open');
      });
  });

  applyFiltersElements.forEach((applyFiltersElement) => {
    applyFiltersElement.addEventListener('click', function() {
      detailsElements.forEach((detailsElement) => {
          detailsElement.removeAttribute('open');
      });
    });
  });
}

document.addEventListener("DOMContentLoaded", handleFacetsDisclosure);

// Handle facet list column count 
function handleFacetListColumnCount() {
  const lists = document.querySelectorAll('.facets__list');
  lists.forEach(list => {
      const items = list.querySelectorAll('li').length;

      if (items > 12) {
          list.style.gridTemplateRows = "repeat(6, 1fr)";
      } else if (items > 6) {
          list.style.gridTemplateRows = "repeat(5, 1fr)";
      } else {
          list.style.gridTemplateRows = "repeat(4, 1fr)";
      }
  });

  const allFacetLists = document.querySelectorAll('.all__facets__list');
  allFacetLists.forEach(list => {
      const items = list.querySelectorAll('li').length;
      list.style.gridTemplateRows = "none";
      list.style.gridAutoFlow = "row";
      if (items > 12) {
          list.style.gridTemplateColumns = "1fr";
      } else if (items > 6) {
          list.style.gridTemplateColumns = "1fr";
      } else {
          list.style.gridTemplateColumns = "1fr";
      }
  });
}

document.addEventListener("DOMContentLoaded", handleFacetListColumnCount);

function getMobileDrawerDetails() {
  return document.querySelector('.mobile-facets__wrapper details');
}

function getMobileDrawerSummary() {
  const drawer = getMobileDrawerDetails();
  return drawer ? drawer.querySelector('summary') : null;
}

function isMobileDrawerOpen() {
  const drawer = getMobileDrawerDetails();
  return drawer ? drawer.hasAttribute('open') : false;
}

function triggerDrawerSummaryClick() {
  const summary = getMobileDrawerSummary();
  if (summary) {
    summary.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
  }
}

function showFilterView() {
  const mobileInner = document.querySelector('.mobile-facets__inner');
  if (mobileInner) {
    mobileInner.style.display = 'flex';
  }
}

function showSortView() {
  const mobileInner = document.querySelector('.mobile-facets__inner');
  if (mobileInner) {
    mobileInner.style.display = 'none';
  }
}

function openMobileDrawer({ showFilters = true } = {}) {
  if (showFilters) {
    showFilterView();
  } else {
    showSortView();
  }

  if (!isMobileDrawerOpen()) {
    triggerDrawerSummaryClick();
  }
}

function closeMobileDrawer() {
  if (isMobileDrawerOpen()) {
    triggerDrawerSummaryClick();
  }
}

function closeAllMobileFilterDetails() {
  document.querySelectorAll('.mobile-facets__details[open]').forEach(details => {
    details.removeAttribute('open');
  });
}

function focusMobileFilter(dataIndex) {
  if (!dataIndex) return;
  const targetDetails = document.querySelector(`.mobile-facets__details[data-index="${dataIndex}"]`);
  if (targetDetails) {
    targetDetails.setAttribute('open', '');
    targetDetails.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

// Handle quick filter buttons configured in the sticky section
document.querySelectorAll('.quick-filter-button').forEach((button) => {
  button.addEventListener('click', (event) => {
    event.preventDefault();
    const filterType = button.dataset.filterType;

    if (filterType === 'sort') {
      closeAllMobileFilterDetails();
      openMobileDrawer({ showFilters: false });
      return;
    }

    if (filterType === 'all') {
      closeAllMobileFilterDetails();
      openMobileDrawer({ showFilters: true });
      return;
    }

    if (filterType === 'filter') {
      // Open the specific filter panel before the drawer so it appears already open
      closeAllMobileFilterDetails();
      focusMobileFilter(button.dataset.filterIndex);
      openMobileDrawer({ showFilters: true });
    }
  });
});

// Handle legacy link-filter (without data-filter-type) - for backward compatibility
document.querySelectorAll('.link-filter:not([data-filter-type])').forEach((element) => {
  element.addEventListener('click', (event) => {
    event.preventDefault();
    openMobileDrawer({ showFilters: true });
  });
});

document.querySelectorAll('.link-sort:not([data-filter-type])').forEach((element) => {
  element.addEventListener('click', (event) => {
    event.preventDefault();
    closeAllMobileFilterDetails();
    openMobileDrawer({ showFilters: false });
  });
});

// Close the drawer when clicking outside the drawer or triggers
document.addEventListener('click', (event) => {
  if (!isMobileDrawerOpen()) return;

  const drawerDetails = getMobileDrawerDetails();
  const clickInsideDrawer = drawerDetails?.contains(event.target);
  const clickOnTrigger = event.target.closest('.quick-filters-bar');

  if (!clickInsideDrawer && !clickOnTrigger) {
    // Let the drawer close first (for smoother animation),
    // then reset the filter state afterwards.
    closeMobileDrawer();
    setTimeout(() => {
      showFilterView();
      closeAllMobileFilterDetails();
    }, 250);
  }
});

const mobielSortoptions = document.querySelectorAll('.facet-filters--mobile .facet-filters__sort option');
const mobileSortselect = document.querySelector('.facet-filters--mobile .facet-filters__sort');
const mobileSortList = document.querySelector('.facet-filters__mobile-sort-list');
const mobileSortform = document.querySelector('.mobile-facets__wrapper facet-filters-form');
const urlParams = new URLSearchParams(window.location.search);
const sortByParam = urlParams.get('sort_by');

mobielSortoptions.forEach((option, index) => {
  const value = option.value;
  const name = option.textContent;
  const li = document.createElement('li');
  const a = document.createElement('a');
  a.textContent = name;
  a.href = '#';
  
  if (!sortByParam && index === 0) {
    a.style.fontWeight = 'bold';
  } else if (sortByParam === value) {
    a.style.fontWeight = 'bold';
  }
  a.addEventListener('click', event => {
    event.preventDefault();
    mobileSortselect.value = value;
    mobileSortselect.addEventListener('change', e => {
      mobileSortform.debouncedOnSubmit(e);
    });
    mobileSortselect.dispatchEvent(new Event('change'));
    mobileSortList.querySelectorAll('a').forEach(a => {
      a.style.fontWeight = 'normal';
    });
    event.target.style.fontWeight = 'bold';
    document.querySelector('.mobile-facets__close').click();
  });
  li.appendChild(a);
  mobileSortList.appendChild(li);
});

function mirrorCheckboxes() {
  const allCheckboxes = document.querySelectorAll(`.facet-checkbox input[type="checkbox"]`);
  allCheckboxes.forEach((checkbox) => {
    checkbox.addEventListener('change', (event) => {
      const checkboxValue = event.target.value;
      //get all checkboxes with the same value as the one that was clicked and mirror the one clicked
      const mirroredCheckboxes = document.querySelectorAll(`.facet-checkbox input[type="checkbox"][value="${checkboxValue}"]`);
      mirroredCheckboxes.forEach((mirroredCheckbox) => {
        mirroredCheckbox.checked = event.target.checked;
      });
    });
  });
}

mirrorCheckboxes();

function updateProductGrid() {
  const singleProductLayout = document.querySelector('#single-product-layout');
  const productGrid = document.querySelector('.product-grid');

  if (singleProductLayout && singleProductLayout.checked && window.innerWidth <= 576) {
    productGrid.classList.remove('grid--2-col-tablet-down');
    productGrid.classList.add('grid--1-col-tablet-down');
  } else {
    productGrid.classList.remove('grid--1-col-tablet-down');
    productGrid.classList.add('grid--2-col-tablet-down');
  }
}

window.addEventListener('scroll', () => {
  const facetsWrapper = document.querySelector('.facets-wrapper');
  const activeFacets = document.querySelector('.active-facets');
  const facetsWrapperRect = facetsWrapper.getBoundingClientRect();
  const headerHeight = document.querySelector('#shopify-section-header').offsetHeight;

  if (facetsWrapperRect.top >= headerHeight) {
    activeFacets.classList.remove('hide-selected-filters');
  } else {
    activeFacets.classList.add('hide-selected-filters');
  }
});

function adjustFacetFieldsetWrapper() {
  const showSelectedFilters = document.querySelector('.show-selected-filters');
  const facetFieldsetWrapper = document.querySelectorAll('.facet-fieldset-wrapper');

  if (showSelectedFilters) {
    const showSelectedFiltersHeight = showSelectedFilters.offsetHeight;
    facetFieldsetWrapper.forEach(wrapper => {
      wrapper.style.paddingTop = `${showSelectedFiltersHeight + 30}px`;
    });
  }
}

adjustFacetFieldsetWrapper();